import React, { useState, useEffect } from "react";
import { CircularProgress } from "@material-ui/core";
import VirList from "src/components/VirList/VirList";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@material-ui/core/Box";
import { Dialog, DialogContent } from "@material-ui/core";
import * as virActions from "src/actions/virActions";
import VirComfirmDialog from "src/components/VirConfirmDialog/VirConfirmDialog";
import * as appActions from "../../actions/appActions";
import { useIsMount } from "src/hooks/useIsMount";
import { athenaURL } from "../../env";
import useStyles from "src/layouts/DigitalVIRLayout/useStyles";
import { RoundedButton, Typography } from "@packages/ui";
import getBrandFromUrl from "src/utils/getBrandFromUrl";
import { ATHENA_VIR_PREPARE_THERMEON } from "src/types/actions";
import getCountryFromURL from "src/utils/getCountryFromUrl";
import brandColors from "@packages/ui/theme/colors";

const LoadingOverlay = ({ loading }) => {
  return (
    loading && (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress />
        <Typography
          variant="h5" // Adjusted font size
          component="div"
          sx={{ mt: 3, fontWeight: 900 }} // Added margin-left
        >
          Loading Vehicle Incident Reports
        </Typography>
      </Box>
    )
  );
};

const DigitalVIRIncidentSelection = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedVirStatusList, setSelectedVirStatusList] = useState([]); // Selected VIR statuses
  const [initLoading, setInitLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startConfirm, setStartConfirm] = useState(false);
  const [dialogVirtualStatus, setDialogVirtualStatus] = useState(false); // Dialog state
  const [selectedType, setSelectedType] = useState(null); // Track the active button
  const [initVirId, setInitVirId] = useState("");
  const [virCreateFlag, setVirCreateFlag] = useState(false);
  const athenaVirServiceTracking = useSelector((state) => state.vir.athenaVirServiceTracking);
  const athenaVirDetail = useSelector((state) => state.vir.athenaVirDetail);
  const athenaVirDetailPatch = useSelector((state) => state.vir.athenaVirDetailPatch);
  const athenaVirAuth = useSelector((state) => state.vir.athenaVirAuth);
  const athenaAuth = useSelector((state) => state.vir.athenaAuth);
  const athenaSource = useSelector((state) => state.vir.athenaSource.source);
  const registration = useSelector((state) => state.vir.virPrepareBE);
  const virsLoading = useSelector((state) => state.vir.athenaVirServiceTracking.loading);
  const isMount = useIsMount();
  const brand = getBrandFromUrl().toLowerCase();
  const [searchParams] = useSearchParams();
  const country = getCountryFromURL();

  useEffect(() => {
    // clean virPrepareTHERMEON photos
    dispatch({
      type: ATHENA_VIR_PREPARE_THERMEON,
      payload: { thirdparty: {}, damage: {}, otherdriver: {}, witness: {} },
    });

    if (
      searchParams.get("source") === "sms" ||
      (searchParams.get("source") === "app" && searchParams.get("vir") !== null)
    ) {
      const virType = searchParams.get("virType");
      dispatch(
        virActions.detailPatchVir({
          virId: searchParams.get("vir"),
          data: {},
        })
      );

      dispatch(
        virActions.detailVir({
          virId: searchParams.get("vir"),
        })
      );

      dispatch(
        virActions.virSelectedId({
          selectedId: searchParams.get("vir"),
        })
      );

      dispatch(
        virActions.virSelectedType({
          selectedType: virType,
        })
      );

      dispatch(
        virActions.resubmitVir({
          virId: searchParams.get("vir"),
          flag: true,
        })
      );
    } else if (athenaVirAuth?.data?.serviceTrackingId) {
      const params = {
        serviceTrackingId: athenaVirAuth.data.serviceTrackingId,
        registration: registration.Registration,
      };
      dispatch(virActions.serviceTrackingVir(params));
      setLoading(true);
    }
  }, []);

  useEffect(() => {
    if (!isMount) {
      if (!virsLoading) {
        setLoading(false);
        dispatch(
          appActions.createToast({
            type: "success",
            message: "Vehicle Incident Reports are loaded",
          })
        );
      }
    }
  }, [virsLoading]);

  useEffect(() => {
    if (!isMount) {
      if (!athenaVirDetail.loading) {
        if (athenaVirDetail.data?._id === searchParams.get("vir")) {
          dispatch(
            virActions.virAppLoading({
              loading: false,
            })
          );
          if (searchParams.get("source") === "sms") {
            navigate(`/vehicleincidents/sms?brand=${brand}`, { replace: true });
          } else if (searchParams.get("source") === "app") {
            navToIncidentReportForm();
          }
        }
      }
    }
  }, [athenaVirDetail.loading]);

  const getFilteredVirs = (type) => {
    const incidents = athenaVirServiceTracking?.data?.virs?.[type];
    const filteredIncidents = incidents?.filter(
      (incident) =>
        incident.virstatus !== "Expired" &&
        incident.virstatus !== "Unavailable" &&
        incident.virstatus !== "Submited" &&
        incident.createSource == "ATHENA-WEBAPP"
    );
    return filteredIncidents ? filteredIncidents : [];
  };

  const navToIncidentReportForm = () => {
    const selectedVirType = searchParams.get("virType");
    if (selectedVirType === "panel") {
      navigate(`/vehicleincidents/vehicleconditionandpolice?brand=${brand}`, {
        replace: true,
      });
    } else if (
      selectedVirType === "windscreen" ||
      selectedVirType === "interior" ||
      selectedVirType === "mechanical"
    ) {
      navigate(`/vehicleincidents/incidentdetails?brand=${brand}`, { replace: true });
    }
  };

  const initVir = async () => {
    const newParams = {
      serviceTrackingId: athenaVirAuth?.data?.serviceTrackingId,
      virType: selectedType,
      registration: registration?.Registration,
      createdBy: athenaSource === "app" ? "staff" : "customer",
      country: country.toUpperCase(),
    };
    setInitLoading(true);
    const response = await fetch(`${athenaURL}/vir/init`, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${athenaAuth.authorization}`,
      },
      body: JSON.stringify(newParams),
    });
    const responseData = await response.json();
    setInitLoading(false);
    dispatch(
      appActions.createToast({
        type: "success",
        message: "Vehicle Incident Report is initialized",
      })
    );
    dispatch(
      virActions.virSelectedId({
        selectedId: responseData.data.virId,
      })
    );

    dispatch(
      virActions.resubmitVir({
        virId: responseData.data.virId,
        flag: false,
      })
    );
    dispatch(virActions.virclean());
    setInitVirId(responseData.data.virId);
    setVirCreateFlag(true);
    if (athenaSource !== "app") {
      console.log("athenaSource", athenaSource);
      setStartConfirm(true);
    }
  };

  useEffect(() => {
    if (initVirId) {
      setVirCreateFlag(true);
      if (athenaSource === "app") {
        startVir();
      }
    }
  }, [initVirId]);

  const startVir = () => {
    setLoading(true);
    dispatch(
      virActions.detailVir({
        virId: initVirId,
      })
    );

    dispatch(
      virActions.detailPatchVir({
        virId: initVirId,
        data: {},
      })
    );
    setStartConfirm(false);
    setLoading(false);
  };

  useEffect(() => {
    if (
      athenaVirDetailPatch.data &&
      !athenaVirDetailPatch.loading &&
      athenaVirDetail.data &&
      !athenaVirDetail.loading &&
      virCreateFlag
    ) {
      console.log("here???");
      navigateToPage();
    }
  }, [loading, athenaVirDetailPatch.data, athenaVirDetail.data, virCreateFlag]);

  const handleCloseStartVirConfirmDialog = () => {
    setStartConfirm(false);
    setDialogVirtualStatus(false);
    setLoading(true);
    setTimeout(function () {
      dispatch(
        virActions.serviceTrackingVir({
          serviceTrackingId: athenaVirAuth.data.serviceTrackingId,
          registration: registration.Registration,
        })
      );
    }, 2000);
    setSelectedVirStatusList(getFilteredVirs(selectedType));
  };

  const handleScreenSelection = (type) => {
    setSelectedType(type);
    dispatch(
      virActions.virSelectedType({
        selectedType: type,
      })
    );

    setDialogVirtualStatus(true);
    const targetVirs = getFilteredVirs(type);
    setSelectedVirStatusList(targetVirs);
  };

  const handleVirDelete = (virid) => {
    dispatch(virActions.deleteVir({ virid: virid }));
  };

  const navigateToPage = () => {
    switch (selectedType) {
      case "windscreen":
        navigate(`/vehicleincidents/incidentdetails?brand=${brand}`);
        break;
      case "panel":
        navigate(`/vehicleincidents/vehicleconditionandpolice?brand=${brand}`);
        break;
      case "theft":
        navigate(`/vehicleincidents/incidentdetails?brand=${brand}`);
        break;
      case "interior":
        navigate(`/vehicleincidents/incidentdetails?brand=${brand}`);
      case "mechanical":
        navigate(`/vehicleincidents/incidentdetails?brand=${brand}`);
      default:
        console.warn(`Unhandled type: ${selectedType}`);
    }
  };

  return (
    <>
      <LoadingOverlay loading={loading} />
      <Box sx={{ p: 1, textAlign: "center", maxWidth: "500px", margin: "0 auto" }}>
        <h2 className={classes.heading}>What type of incident are you reporting?</h2>

        <hr
          className={classes.breakLine}
          style={{ borderColor: brandColors[brand] }} // since the actural brand maybe different, recover the default setting dynamiclly
        />

        {["windscreen", "panel", "interior", "mechanical", "theft"].map((type) => (
          <Box key={type} sx={{ my: 3 }}>
            {type !== "theft" && (
              <RoundedButton
                onClick={() => handleScreenSelection(type)}
                variant="contained"
                fullWidth
              >
                {type === "windscreen"
                  ? "Windscreen or tyre damage"
                  : type === "panel"
                  ? "Panel damage or vehicle accident"
                  : type === "interior"
                  ? "Interior or missing items"
                  : type === "mechanical"
                  ? "Mechanical"
                  : "Vehicle theft"}
              </RoundedButton>
            )}
            {selectedType === type && (
              <Dialog
                open={dialogVirtualStatus}
                onClose={() => setDialogVirtualStatus(false)}
                fullWidth
                maxWidth="md"
                key={type}
              >
                <DialogContent>
                  <VirList
                    virs={selectedVirStatusList}
                    createLoading={initLoading}
                    handleDelete={handleVirDelete}
                    handleCreate={initVir}
                  />
                </DialogContent>
              </Dialog>
            )}
          </Box>
        ))}

        <VirComfirmDialog
          open={startConfirm && initVirId !== ""}
          handleClose={handleCloseStartVirConfirmDialog}
          content="Click ‘OK’ to begin submitting a vehicle incident report."
          execute={startVir}
          loading={athenaVirDetail.loading}
        />
      </Box>
    </>
  );
};

export default DigitalVIRIncidentSelection;
